import { Divider, Stack } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import type { CampaignImpactKpi, CampaignImpactMetadata } from "../../types";
import { getMetricBoxConfig } from "../../constants";
import styles from "./CampaignImpact.module.css";
import { useAuth } from "../../hooks/useAuth";
import { roundKpiValue } from "../../utils/campaignImpact";
import {
  CampaignImpactInfoBoxCardAllUnavailable,
  CampaignImpactInfoBoxCardError,
  CampaignImpactInfoBoxCardInconclusive,
  CampaignImpactInfoBoxCardSomeUnavailable,
  CampaignImpactInfoBoxCardTrendingDown,
} from "./CampaignImpactInfoBoxCard";

type CampaignImpactInfoBoxProps = {
  kpis: CampaignImpactKpi[];
  metadata?: CampaignImpactMetadata;
  isError?: boolean;
};

export const CampaignImpactInfoBox = ({ kpis, metadata, isError }: CampaignImpactInfoBoxProps) => {
  const { isAirlineUser } = useAuth();
  const i18n = useI18n();
  const displayedMetricNames = getMetricBoxConfig(i18n).map((config) => config.metricName);

  if (isError) {
    return (
      <>
        <Divider className={styles.divider} />
        <CampaignImpactInfoBoxCardError />
      </>
    );
  }

  const inconclusiveDataForAirlineUsers = isAirlineUser && !metadata?.statisticallySignificant;

  const availableKpis = kpis.filter((k) => {
    // If data is not statistically significant, it considers as unavailable for airline users
    if (inconclusiveDataForAirlineUsers) {
      return false;
    }

    return displayedMetricNames.some((name) => name === k.name) && k.value !== null && k.value !== undefined;
  });

  const unavailableCount = displayedMetricNames.length - availableKpis.length;
  // Show some data isn't available for airline users when data is inconclusive
  const allUnavailable = availableKpis.length === 0 && !inconclusiveDataForAirlineUsers;
  const someUnavailable = unavailableCount > 0 && !allUnavailable;
  // Only flag trending-down when the *displayed* (rounded) value is negative, so e.g. -0.04 → 0.0% doesn't trigger the banner.
  const hasTrendingDown = availableKpis.some((k) => k.value != null && roundKpiValue(k.value, k.name) < 0);
  const inconclusive = !isAirlineUser && !metadata?.statisticallySignificant;

  if (inconclusive) {
    return (
      <>
        <Divider className={styles.divider} />
        <CampaignImpactInfoBoxCardInconclusive />
      </>
    );
  }

  if (allUnavailable) {
    return (
      <>
        <Divider className={styles.divider} />
        <CampaignImpactInfoBoxCardAllUnavailable />
      </>
    );
  }

  const showUnavailableBox = someUnavailable;
  const showTrendingDownBox = hasTrendingDown;

  if (!showUnavailableBox && !showTrendingDownBox) {
    return null;
  }

  return (
    <>
      <Divider className={styles.divider} />
      <Stack direction="row" gap={4} alignItems="stretch">
        {showTrendingDownBox && (
          <Stack.Item grow>
            <CampaignImpactInfoBoxCardTrendingDown />
          </Stack.Item>
        )}
        {showUnavailableBox && (
          <Stack.Item grow>
            <CampaignImpactInfoBoxCardSomeUnavailable />
          </Stack.Item>
        )}
      </Stack>
    </>
  );
};
